import Trans from 'next-translate/Trans';
import useTranslation from 'next-translate/useTranslation';
import { useFormContext } from 'react-hook-form';

// Components
import FormFieldMessage from '@/components/base/FormFieldMessage';
import ButtonGoogleSignIn from '@/components/composite/Button/ButtonGoogleSignIn';
import Button from '@/components/base/Button';
import Label from '@/components/base/Label';
import Checkbox from '@/components/base/Checkbox';
import LinkBase from '@/components/base/LinkBase';

// Hooks
import useMarketingUrl from '@/utils/urls/useMarketingUrl';

// Type
import {
    SignUpFormType,
    SignUpFormPropsType
} from '@/components/pages/signup/createAccount/SignUpForm';

type SignUpMethodChoiceFormType = {
    setIsWithEmail: SignUpFormPropsType['setIsWithEmail'];
};

const SignUpMethodChoiceForm = ({ setIsWithEmail }: SignUpMethodChoiceFormType) => {
    const { t } = useTranslation('signup_funnel');
    const { marketingUrl } = useMarketingUrl();
    const {
        register,
        setError,
        getValues,
        clearErrors,
        watch,
        formState: { errors }
    } = useFormContext<SignUpFormType>();

    return (
        <>
            <Button
                id="signup-with-email"
                type="button"
                className="mb-3"
                icon="email-clicks"
                onClick={() => {
                    if (getValues('terms')) {
                        setIsWithEmail(true);
                    } else {
                        setError('terms', {
                            type: 'manual',
                            message: t('create_account.error_terms')
                        });
                    }
                }}>
                {t('create_account.button_signup_with_email')}
            </Button>
            <ButtonGoogleSignIn
                id="signup-with-google"
                type="sign_up"
                isAuthorize={() => {
                    if (!getValues('terms')) {
                        setError('terms', {
                            type: 'manual',
                            message: t('create_account.error_terms')
                        });
                        return false;
                    }
                    return true;
                }}
                queryParameters={watch('newsletter') ? { newsletter: 1 } : null}
            />
            <Label fontType="legalCheckbox" className="relative mt-5 pl-5">
                <Checkbox
                    id="newsletter"
                    boxSize="sm"
                    className="absolute left-0 top-0.5"
                    {...register('newsletter')}
                />
                {t('create_account.checkbox_newsletter')}
            </Label>
            <Label required fontType="legalCheckbox" className="relative mt-5 pl-5">
                <Checkbox
                    id="terms"
                    error={Boolean(errors?.terms)}
                    aria-describedby="terms_error"
                    boxSize="sm"
                    className="absolute left-0 top-0.5"
                    onClick={() => {
                        if (errors.terms) {
                            clearErrors('terms');
                        }
                    }}
                    {...register('terms', {
                        required: true
                    })}
                />
                <Trans
                    i18nKey="signup_funnel:create_account.checkbox_terms"
                    components={[
                        <LinkBase
                            key="terms"
                            href={marketingUrl('terms_of_use')}
                            target="_blank"
                            className="underline"
                            rel="noopener noreferrer"
                        />,
                        <LinkBase
                            key="subscription"
                            href={marketingUrl('terms_of_subscription')}
                            target="_blank"
                            className="underline"
                            rel="noopener noreferrer"
                        />
                    ]}
                />
            </Label>
            {errors.terms ? (
                <div className="mt-1">
                    <FormFieldMessage id="terms_error" className="inline-block">
                        {t('create_account.error_terms')}
                    </FormFieldMessage>
                </div>
            ) : null}
        </>
    );
};

export default SignUpMethodChoiceForm;
